import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Handshake, Truck, CheckCircle2, XCircle, Loader2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, errMsg, eur } from "@/lib/api";
import { PageShell, Photo } from "@/components/common";
import { DeliveryQuoteForm } from "@/components/DeliveryQuoteForm";

function Row({ label, value, strong, testid }) {
  return <div className={`flex justify-between py-2 ${strong ? "font-semibold text-lg border-t border-stone-200 mt-2 pt-4" : "text-sm"}`}><span>{label}</span><span data-testid={testid}>{value}</span></div>;
}

export default function Checkout() {
  const { listingId } = useParams();
  const api = useApi();
  const idem = useRef(crypto.randomUUID());
  const [l, setL] = useState(null);
  const [mode, setMode] = useState("handover");
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [wallet, setWallet] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/listings/${listingId}`).then((r) => setL(r.data));
    api.get("/config").then((r) => setCfg(r.data));
    api.get("/wallet").then((r) => setWallet(r.data.balance));
  }, [api, listingId]);

  if (!l || !cfg) return <PageShell><p className="text-stone-500">Chargement…</p></PageShell>;
  if (l.my_reservation?.status !== "accepted") return <PageShell><p data-testid="checkout-no-reservation">Une réservation acceptée est nécessaire avant le paiement. <Link to={`/annonce/${l.id}`} className="underline">Retour à l'annonce</Link></p></PageShell>;

  const price = l.type === "donation" ? 0 : l.my_reservation.agreed_price || l.price;
  const commission = l.type === "sale" ? Math.round(price * cfg.commission_rate * 100) / 100 : cfg.donation_fee;
  const delivery = mode === "delivery" ? quote?.client_price_ttc || 0 : 0;
  const total = Math.round((price + commission + delivery) * 100) / 100;
  const walletPart = useWallet ? Math.min(wallet, total) : 0;

  const pay = async () => {
    setBusy(true);
    try {
      const { data: o } = await api.post("/orders", { listing_id: l.id, delivery_quote_id: mode === "delivery" ? quote?.id : null }, { headers: { "Idempotency-Key": idem.current } });
      setOrder(o);
      const { data } = await api.post("/payments/checkout", { order_id: o.id, origin_url: window.location.origin, accept_terms: terms, use_wallet: useWallet });
      if (data.paid_with_wallet) return navigate(`/paiement/succes?order_id=${o.id}`);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      idem.current = crypto.randomUUID();
      setBusy(false);
    }
  };

  return (
    <PageShell eyebrow="Paiement sécurisé" title="Récapitulatif">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-6">
          <div className="flex gap-4 items-center bg-white rounded-2xl border border-stone-200 p-4">
            <Photo src={l.photos?.[0]} alt="" className="w-20 h-20 rounded-xl" />
            <div><p className="font-medium" data-testid="checkout-listing-title">{l.title}</p><p className="text-sm text-stone-500">{l.city}</p></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {(
              <button data-testid="checkout-mode-handover" onClick={() => setMode("handover")} className={`rounded-2xl border p-5 text-left transition-colors ${mode === "handover" ? "border-forest bg-forest/5" : "border-stone-200 bg-white"}`}>
                <Handshake className="w-5 h-5 text-forest" /><p className="font-semibold mt-2">Remise en main propre</p><p className="text-xs text-stone-500">Adresse : {l.exact_address}</p>
              </button>
            )}
            <button data-testid="checkout-mode-delivery" onClick={() => setMode("delivery")} className={`rounded-2xl border p-5 text-left transition-colors ${mode === "delivery" ? "border-forest bg-forest/5" : "border-stone-200 bg-white"}`}>
              <Truck className="w-5 h-5 text-forest" /><p className="font-semibold mt-2">Livraison / Point relais</p><p className="text-xs text-stone-500">Coursier, Mondial Relay, Chronopost (simulés)</p>
            </button>
          </div>
          {mode === "delivery" && <div className="bg-white rounded-2xl border border-stone-200 p-6"><DeliveryQuoteForm listing={l} onSelect={setQuote} selectedId={quote?.id} /></div>}
        </div>
        <div className="lg:col-span-5">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 lg:sticky lg:top-24 space-y-2" data-testid="checkout-summary">
            <Row label={l.type === "donation" ? "Objet (don)" : "Prix de l'objet"} value={eur(price)} testid="summary-price" />
            <Row label={l.type === "sale" ? `Frais de service (${Math.round(cfg.commission_rate * 100)} %)` : "Frais de don (forfait)"} value={eur(commission)} testid="summary-commission" />
            <Row label="Livraison" value={mode === "delivery" ? (quote ? eur(delivery) : "à choisir") : "—"} testid="summary-delivery" />
            <Row label="Total TTC" value={eur(price + commission + delivery)} strong testid="summary-total" />
            {wallet > 0 && (
              <label className="flex items-center justify-between gap-2 rounded-xl bg-sun/30 p-3 text-sm" data-testid="wallet-option">
                <span className="flex items-center gap-2"><Checkbox data-testid="use-wallet-checkbox" checked={useWallet} onCheckedChange={(v) => setUseWallet(!!v)} /><Wallet className="w-4 h-4" />Utiliser mon porte-monnaie (solde {eur(wallet)})</span>
              </label>
            )}
            {useWallet && <>
              <Row label="Porte-monnaie" value={`− ${eur(walletPart)}`} testid="summary-wallet" />
              <Row label="Reste à payer par carte" value={eur(total - walletPart)} strong testid="summary-remaining" />
            </>}
            <p className="text-xs text-stone-500">Le prix final est confirmé par le serveur (offre acceptée éventuelle incluse). Tous les frais sont à la charge de l'acheteur. Votre paiement est conservé par labradedie.fr et n'est versé au vendeur qu'après votre validation de la réception.</p>
            <label className="flex items-start gap-2 text-sm pt-3">
              <Checkbox data-testid="checkout-terms-checkbox" checked={terms} onCheckedChange={(v) => setTerms(!!v)} />
              <span>J'accepte les <Link to="/legal/cgv" className="underline" target="_blank">CGV</Link> et la politique de remboursement.</span>
            </label>
            <Button data-testid="checkout-pay-btn" disabled={!terms || busy || (mode === "delivery" && !quote)} onClick={pay} className="w-full rounded-full h-12 bg-terra hover:bg-terra/90 mt-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" />{useWallet && walletPart >= total ? "Payer avec mon porte-monnaie" : "Payer avec Stripe"}</>}
            </Button>
            <p className="text-[11px] text-stone-500 text-center">Carte saisie chez Stripe — nous ne stockons aucun numéro. Mode test : 4242 4242 4242 4242.</p>
            {order && <p className="text-[11px] text-stone-400">Commande {order.id.slice(0, 8)}</p>}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const api = useApi();
  const [state, setState] = useState({ status: "checking" });
  const sid = params.get("session_id");
  const orderId = params.get("order_id");

  useEffect(() => {
    let n = 0;
    let stop = false;
    const poll = async () => {
      try {
        if (orderId) {
          const { data: o } = await api.get(`/orders/${orderId}`);
          if (o.status !== "pending_payment") return !stop && setState({ status: "paid", order: o });
        } else {
          const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          const { data: o } = await api.get(`/orders/${data.order_id}`);
          return !stop && setState({ status: "paid", order: o });
        }
        if (data.status === "expired") return setState({ status: "expired" });
        }
      } catch {}
      if (++n < 15 && !stop) setTimeout(poll, 2000); else setState({ status: "timeout" });
    };
    poll();
    return () => { stop = true; };
  }, [api, sid, orderId]);

  const o = state.order;
  return (
    <PageShell>
      <div className="max-w-xl mx-auto bg-white rounded-2xl border border-stone-200 p-8 text-center" data-testid="payment-result">
        {state.status === "checking" && <><Loader2 className="w-10 h-10 animate-spin mx-auto text-forest" /><p className="mt-4">Vérification du paiement…</p></>}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
            <h1 className="font-serif text-4xl text-forest mt-4" data-testid="payment-success-title">Paiement confirmé</h1>
            <div className="text-left mt-6 text-sm space-y-1" data-testid="payment-receipt">
              <p className="eyebrow mb-2">Reçu</p>
              <Row label={o.listing_title} value={eur(o.item_price)} />
              <Row label="Frais de service" value={eur(o.commission)} />
              {o.delivery_fee > 0 && <Row label="Livraison" value={eur(o.delivery_fee)} />}
              <Row label="Total" value={eur(o.buyer_total)} strong />
              {o.wallet_used > 0 && <Row label="Dont porte-monnaie" value={eur(o.wallet_used)} />}
              {o.pickup_address && <p className="pt-2">Adresse de remise : <b>{o.pickup_address}</b></p>}
            </div>
            <div className="flex gap-3 justify-center mt-6 flex-wrap">
              {o.delivery && <Link to={`/livraison/${o.delivery.id}`}><Button data-testid="payment-track-delivery-btn" className="rounded-full bg-forest">Suivre la livraison</Button></Link>}
              <Link to="/compte?tab=orders"><Button data-testid="payment-orders-btn" variant="outline" className="rounded-full">Mes commandes</Button></Link>
            </div>
          </>
        )}
        {["expired", "timeout"].includes(state.status) && <><XCircle className="w-12 h-12 mx-auto text-red-500" /><p className="mt-4" data-testid="payment-failed-msg">Paiement non confirmé. Vérifiez vos commandes ou réessayez.</p></>}
      </div>
    </PageShell>
  );
}

export function PaymentCancel() {
  return (
    <PageShell>
      <div className="max-w-xl mx-auto bg-white rounded-2xl border border-stone-200 p-8 text-center" data-testid="payment-cancel">
        <XCircle className="w-12 h-12 mx-auto text-stone-400" />
        <h1 className="font-serif text-4xl text-forest mt-4">Paiement annulé</h1>
        <p className="text-stone-600 mt-2">Aucun débit n'a été effectué. Votre réservation reste valable jusqu'à son expiration.</p>
        <Link to="/compte?tab=reservations"><Button className="rounded-full bg-forest mt-6">Mes réservations</Button></Link>
      </div>
    </PageShell>
  );
}
